<template>
  <div class="overflow-y-auto">
    <DataView
      lazy
      dataKey="id"
      :value="dataObj.list_items || []"
      layout="grid"
    >
      <template #empty>No items found.</template>
      <template #grid="slotProps">
        <div class="grid grid-cols-12 gap-3">
          <div
            v-for="(item, index) in slotProps.items"
            :key="index"
            class="col-span-12 p-2"
            :class="{
              [`sm:col-span-${dataObj.sm_cols}`]: dataObj.sm_cols,
              [`md:col-span-${dataObj.md_cols}`]: dataObj.md_cols,
              [`xl:col-span-${dataObj.xl_cols}`]: dataObj.xl_cols
            }"
          >
            <div
              class="p-3 border border-surface-200 bg-surface-0 rounded flex flex-col cursor-pointer"
              @click="onCardClick(item, index)"
            >
              <div class="flex items-start">
                <div class="flex items-center w-full">
                  <div class="pl-4 flex-1 flex flex-col">
                    <span class="font-bold text-lg text-surface-500">
                      {{ item.title }}
                    </span>
                    <div
                      v-if="item.showQuestionCount !== false"
                      class="flex w-full text-surface-500 gap-3"
                    >
                      <span>
                        {{ `Total : ${item.totalQuestions || 0}` }}
                      </span>
                      <span>
                        {{ `Answered : ${item.answeredQuestions || 0}` }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </DataView>
  </div>

  <!-- Question Dialog -->
  <QuestionDialog
    :visible="questionDialog"
    :cardItem="selectedCardItem"
    :cardListPageId="dataObj.id"
    :cardList="dataObj.list_items"
    :currentCardIndex="selectedCardIndex"
    :templateData="templateData"
    @update:visible="questionDialog = $event"
    @save="handleQuestionSave"
    @navigate="handleCardNavigation"
  />

  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />

  <FullScreenOverlayLoader
    :isLoading="loaderObj.isLoading"
    :message="loaderObj.loaderMsg"
  />
</template>

<script>
  import { defineComponent, ref, onMounted } from "vue";
  import QuestionDialog from "@/components/QuestionDialog.vue";
  import { useFormTemplateResponseStore } from "@/stores/formTemplateResponseStore";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";

  export default defineComponent({
    name: "CardListComponent",
    components: {
      QuestionDialog,
      ErrorDialog,
      FullScreenOverlayLoader
    },
    props: {
      dataObj: {
        type: Object,
        required: false,
        default: () => ({})
      },
      templateData: {
        type: Object,
        required: false,
        default: null
      }
    },
    setup(props, { emit }) {
      const questionDialog = ref(false);
      const selectedCardItem = ref({});
      const selectedCardIndex = ref(0);
      const formTemplateResponseStore = useFormTemplateResponseStore();

      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });

      const loaderObj = ref({
        isLoading: false,
        loaderMsg: ""
      });

      const onCardClick = (item, index) => {
        selectedCardItem.value = item;
        selectedCardIndex.value = index;

        questionDialog.value = true;
        emit("card-selected", { item, index });
      };

      const handleQuestionSave = (saveData) => {
        saveCardData(saveData);
        updateAnsweredCount(saveData.page_id);
      };

      const saveCardData = (saveData) => {
        try {
          const cardId = findCardId(saveData.page_id);
          if (!cardId) return;

          const response = formTemplateResponseStore.response;
          const templateId = formTemplateResponseStore.templateId;
          const cardListPageId = props.dataObj.id;

          if (!response[templateId]) response[templateId] = {};
          if (!response[templateId][cardListPageId])
            response[templateId][cardListPageId] = {};

          response[templateId][cardListPageId][cardId] = saveData.data;
        } catch (error) {
          console.error("Save card data error:", error);
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = "Failed to save card data";
        }
      };

      const findCardId = (pageId) => {
        return props.dataObj.list_items.find((item) => item.page_id === pageId)
          ?.id;
      };

      const updateAnsweredCount = (pageId) => {
        try {
          const cardId = findCardId(pageId);
          if (!cardId) return;

          const cardItem = props.dataObj.list_items.find(
            (item) => item.id === cardId
          );
          if (!cardItem) return;

          const cardListPageId = props.dataObj.id;
          const responseData =
            formTemplateResponseStore.response[
              formTemplateResponseStore.templateId
            ]?.[cardListPageId]?.[cardId];

          const answeredCount = Object.values(responseData || {}).filter(
            (val) => val !== null && val !== undefined && val !== ""
          ).length;

          cardItem.answeredQuestions = answeredCount;
        } catch (error) {
          console.error("Update answered count error:", error);
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = "Failed to update answered count";
        }
      };

      onMounted(() => {
        props.dataObj.list_items?.forEach((item) => {
          updateAnsweredCount(item.page_id);
        });
      });

      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };

      const handleCardNavigation = (direction) => {
        const currentIndex = selectedCardIndex.value;
        const maxIndex = props.dataObj.list_items.length - 1;

        const newIndex =
          direction === "prev"
            ? Math.max(0, currentIndex - 1)
            : Math.min(maxIndex, currentIndex + 1);

        selectedCardIndex.value = newIndex;
        selectedCardItem.value = props.dataObj.list_items[newIndex];
      };

      return {
        questionDialog,
        selectedCardItem,
        selectedCardIndex,
        onCardClick,
        handleQuestionSave,
        errorObj,
        loaderObj,
        onErrorDialogClose,
        handleCardNavigation
      };
    }
  });
</script>
